import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader, Mail } from 'lucide-react';
import axios from 'axios';
import { getApiUrl } from '../config/api';

const ApproveEmail = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    approveEmail();
  }, [token]);

  const approveEmail = async () => {
    try {
      const response = await axios.get(getApiUrl(`/api/entries/approve-email/${token}`));
      
      if (response.data.success) {
        setEmail(response.data.email);
        setStatus('success');
      } else {
        setError(response.data.error || 'Failed to approve email');
        setStatus('error');
      }
    } catch (error) {
      console.error('Error approving email:', error);
      setError(error.response?.data?.error || 'Failed to approve email');
      setStatus('error');
    }
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Confirming your email...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Email Confirmation Failed</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={handleGoToDashboard}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Confirmed!</h2>
              <p className="text-gray-600 mb-4">
                Your email address <span className="font-semibold text-gray-900">{email}</span> has been successfully confirmed.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">What's Next?</p>
                  <p>Return to MyTrailer to complete the sync process. Your entries, results, and memberships will be synced once you click "Continue" in the setup wizard.</p>
                </div>
              </div>
            </div>

            <button
              onClick={handleGoToDashboard}
              className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Return to MyTrailer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ApproveEmail;

